import React, { useEffect, useState } from "react";
import { useRefreshTick } from "@/lib/autoRefresh";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah, formatDate, PAYMENT_TYPE_LABEL } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { ReceiptText, Truck, AlertTriangle, Clock, CalendarClock, FileText } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BILL_ROLES = ["staf_tagihan", "direktur", "tim_keuangan", "it_admin"];
const VEHICLE_ROLES = ["tim_kendaraan", "direktur", "it_admin"];

export function DashboardModules({ user, only }) {
  const navigate = useNavigate();
  const [bills, setBills] = useState(null);
  const [vehicles, setVehicles] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [billList, setBillList] = useState(null);

  const showBills = BILL_ROLES.includes(user?.role) && only !== "vehicles";
  const showVehicles = VEHICLE_ROLES.includes(user?.role) && only !== "bills";
  const canRequestCash = ["pemohon", "tim_kendaraan"].includes(user?.role);
  const [selected, setSelected] = useState({});
  const toggle = (k) => setSelected((p) => ({ ...p, [k]: !p[k] }));

  const tick = useRefreshTick();
  useEffect(() => {
    if (showBills) api.get("/vendor-bills/dashboard").then((r) => setBills(r.data)).catch(() => {});
    if (showVehicles) api.get("/vehicles/dashboard").then((r) => setVehicles(r.data)).catch(() => {});
  }, [showBills, showVehicles, tick]);

  if (!showBills && !showVehicles) return null;

  return (
    <div className="space-y-4">
      {showBills && bills && (
        <Card className="p-4" data-testid="dashboard-bills-module">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ReceiptText className="h-5 w-5 text-brand" />
              <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Tagihan Vendor</h3>
            </div>
            <button onClick={() => navigate("/vendor-bills")} className="text-xs text-brand hover:underline">Kelola tagihan</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <MiniStat label="Belum Dibayar" value={bills.unpaid_count} sub={formatRupiah(bills.total_unpaid)} accent="text-slate-700" />
            <MiniStat label="Jatuh Tempo <14 hari" value={bills.upcoming.length} icon={Clock} accent="text-amber-600" testId="dash-bills-upcoming-btn" onClick={() => setBillList({ title: "Jatuh Tempo < 14 Hari", rows: bills.upcoming })} />
            <MiniStat label="Terlambat" value={bills.overdue.length} icon={AlertTriangle} accent="text-rose-600" testId="dash-bills-overdue-btn" onClick={() => setBillList({ title: "Tagihan Terlambat", rows: bills.overdue })} />
            <MiniStat label="BG Segera Efektif" value={bills.bg_upcoming.length} icon={CalendarClock} accent="text-sky-600" testId="dash-bills-bg-btn" onClick={() => setBillList({ title: "BG Segera Efektif (≤ 30 hari)", rows: bills.bg_upcoming })} />
          </div>
          <BillListDialog list={billList} onClose={() => setBillList(null)} />
          <div className="space-y-2">
            {[...bills.overdue, ...bills.upcoming].slice(0, 5).map((b) => (
              <div key={b.id} data-testid={`dash-bill-${b.id}`} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{b.vendor_name}</p>
                  <p className="text-xs text-muted-foreground">{PAYMENT_TYPE_LABEL[b.payment_type]} · Jatuh tempo {formatDate(b.due_date)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold font-mono">{formatRupiah(b.amount)}</p>
                  <p className={`text-[11px] font-medium ${b.days_to_due < 0 ? "text-rose-600" : "text-amber-600"}`}>
                    {b.days_to_due < 0 ? `Terlambat ${Math.abs(b.days_to_due)} hari` : `${b.days_to_due} hari lagi`}
                  </p>
                </div>
              </div>
            ))}
            {bills.overdue.length + bills.upcoming.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">Tidak ada tagihan yang mendekati jatuh tempo.</p>
            )}
          </div>
        </Card>
      )}

      {showVehicles && vehicles && (
        <Card className="p-4" data-testid="dashboard-vehicles-module">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-brand" />
              <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Kendaraan per Cabang</h3>
            </div>
            <button onClick={() => navigate("/vehicles")} className="text-xs text-brand hover:underline">Kelola kendaraan</button>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <MiniStat label="Total Kendaraan" value={vehicles.total_vehicles} accent="text-slate-700" />
            <MiniStat label="Reminder Segera" value={vehicles.total_upcoming} icon={Clock} accent="text-amber-600" />
            <MiniStat label="Terlambat" value={vehicles.total_overdue} icon={AlertTriangle} accent="text-rose-600" />
          </div>
          {vehicles.age_warnings?.length > 0 && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="age-warning-list">
              <p className="text-xs font-semibold text-red-700 flex items-center gap-1.5 mb-2"><AlertTriangle className="h-4 w-4" /> Kendaraan ≥ 8 Tahun — Perlu Evaluasi ({vehicles.age_warnings.length})</p>
              <div className="space-y-1">
                {vehicles.age_warnings.slice(0, 3).map((a) => (
                  <button key={a.vehicle_id} onClick={() => navigate(`/vehicles/${a.vehicle_id}`)} data-testid={`age-warning-${a.vehicle_id}`}
                    className="w-full flex items-center justify-between text-sm hover:bg-red-100 rounded px-2 py-1 transition-colors text-left">
                    <span className="font-medium truncate">{a.plate_number} · {a.branch}</span>
                    <span className="text-xs text-red-700 font-semibold shrink-0">{a.vehicle_age} tahun</span>
                  </button>
                ))}
                {vehicles.age_warnings.length > 3 && (
                  <button onClick={() => navigate("/vehicles")} className="text-[11px] text-red-700 hover:underline px-2" data-testid="age-warning-more">+{vehicles.age_warnings.length - 3} kendaraan lainnya</button>
                )}
              </div>
            </div>
          )}
          {vehicles.branches.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Belum ada data kendaraan.</p>
          ) : null}
          {vehicles.items && vehicles.items.length > 0 && (() => {
            const selItems = vehicles.items.filter((it) => selected[`${it.vehicle_id}-${it.key}`]);
            const selTotal = selItems.reduce((s, it) => s + (it.cost || 0), 0);
            const submitCombined = () => {
              const reason = selItems
                .map((it) => `${it.label} kendaraan ${it.plate_number} (${it.branch}) — jatuh tempo ${formatDate(it.due_date)}`)
                .join("\n");
              navigate("/requests/new", { state: {
                prefillAmount: selTotal,
                prefillReason: reason,
                vehicleRefs: selItems.map((it) => ({ vehicle_id: it.vehicle_id, plate_number: it.plate_number, action: it.label })),
              } });
            };
            return (
            <div className="mt-5" data-testid="dashboard-vehicle-due-list">
              <div className="flex items-center gap-2 mb-2">
                <CalendarClock className="h-4 w-4 text-amber-600" />
                <p className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>Akan/Perlu Ditindak — Pajak, KIR, Servis, Oli</p>
                {canRequestCash && <span className="text-[11px] text-muted-foreground ml-auto">Centang untuk ajukan beberapa sekaligus</span>}
              </div>
              <div className={`space-y-2 ${showAll ? "max-h-[420px] overflow-y-auto pr-1" : ""}`}>
                {(showAll ? vehicles.items : vehicles.items.slice(0, 5)).map((it) => {
                  const key = `${it.vehicle_id}-${it.key}`;
                  return <DueActionItem key={key} it={it} canRequestCash={canRequestCash} navigate={navigate} checked={!!selected[key]} onToggle={toggle} />;
                })}
              </div>
              {vehicles.items.length > 5 && (
                <button onClick={() => setShowAll((s) => !s)} className="mt-2 w-full text-xs text-brand hover:underline" data-testid="vehicle-due-toggle-all">
                  {showAll ? "Tampilkan lebih sedikit" : `Tampilkan semua (${vehicles.items.length})`}
                </button>
              )}
              {canRequestCash && selItems.length > 0 && (
                <div className="mt-3 rounded-lg border-2 border-brand bg-brand/5 p-3 flex flex-wrap items-center justify-between gap-2" data-testid="multi-select-bar">
                  <div>
                    <p className="text-xs text-muted-foreground">{selItems.length} kendaraan dipilih</p>
                    <p className="text-lg font-bold text-brand font-mono" style={{ fontFamily: "Outfit" }} data-testid="multi-select-total">{formatRupiah(selTotal)}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setSelected({})} data-testid="multi-clear-btn"
                      className="text-sm px-3 py-2 rounded-lg border hover:bg-secondary transition-colors">Bersihkan</button>
                    <button onClick={submitCombined} data-testid="multi-request-cash-btn"
                      className="text-sm font-medium px-4 py-2 rounded-lg bg-brand text-white hover:bg-emerald-800 transition-colors inline-flex items-center gap-1.5">
                      <FileText className="h-4 w-4" /> Buat Pengajuan Kas Gabungan
                    </button>
                  </div>
                </div>
              )}
              <div className="mt-3 rounded-lg bg-secondary/60 border p-3 flex flex-wrap items-center justify-between gap-2" data-testid="vehicle-est-total">
                <div>
                  <p className="text-xs text-muted-foreground">Total perkiraan biaya (referensi permintaan kas)</p>
                  <p className="text-lg font-bold text-brand font-mono" style={{ fontFamily: "Outfit" }}>{formatRupiah(vehicles.total_est_upcoming)}</p>
                </div>
                {user?.role === "tim_kendaraan" && (
                  <button onClick={() => navigate("/requests/new")} data-testid="vehicle-request-cash-btn"
                    className="text-sm font-medium px-4 py-2 rounded-lg bg-brand text-white hover:bg-emerald-800 transition-colors">
                    Ajukan Kas
                  </button>
                )}
              </div>
            </div>
            );
          })()}
        </Card>
      )}
    </div>
  );
}

function DueActionItem({ it, canRequestCash, navigate, checked, onToggle }) {
  const [open, setOpen] = useState(false);
  const key = `${it.vehicle_id}-${it.key}`;
  const goDetail = () => { setOpen(false); navigate(`/vehicles/${it.vehicle_id}`); };
  const goRequest = () => {
    setOpen(false);
    navigate("/requests/new", {
      state: {
        prefillAmount: it.cost || 0,
        prefillReason: `${it.label} kendaraan ${it.plate_number} (${it.branch}) — jatuh tempo ${formatDate(it.due_date)}`,
        vehicleRefs: [{ vehicle_id: it.vehicle_id, plate_number: it.plate_number, action: it.label }],
      },
    });
  };
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${checked ? "border-brand bg-brand/5" : "hover:border-brand/60"}`}>
      {canRequestCash && (
        <Checkbox checked={checked} onCheckedChange={() => onToggle(key)} data-testid={`due-check-${key}`} aria-label={`Pilih ${it.plate_number}`} />
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid={`due-item-${key}`} className="flex-1 flex items-center justify-between gap-3 text-left min-w-0">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{it.plate_number} · {it.label}</p>
              <p className="text-xs text-muted-foreground truncate">{it.branch} · jatuh tempo {formatDate(it.due_date)}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-semibold font-mono">{formatRupiah(it.cost)}</p>
              <p className={`text-[11px] font-medium ${it.status === "overdue" ? "text-rose-600" : it.status === "perlu_diskusi" ? "text-amber-700" : "text-amber-600"}`}>
                {it.status === "overdue" ? `Terlambat ${Math.abs(it.days)} hr` : it.status === "perlu_diskusi" ? "Perlu Diskusi" : `${it.days} hari lagi`}
              </p>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-60 p-1.5" data-testid={`due-item-actions-${key}`}>
          <button onClick={goDetail} data-testid={`due-action-detail-${key}`}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-secondary transition-colors text-left">
            <Truck className="h-4 w-4 text-brand shrink-0" /> Lihat Detail Kendaraan
          </button>
          {canRequestCash && (
            <button onClick={goRequest} data-testid={`due-action-request-${key}`}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-secondary transition-colors text-left">
              <FileText className="h-4 w-4 text-brand shrink-0" /> Buat Pengajuan Kas
            </button>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function MiniStat({ label, value, sub, icon: Icon, accent, onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} data-testid={testId}
      className={`rounded-lg border p-3 text-left ${onClick ? "hover:bg-secondary/60 hover:border-brand/40 transition-colors cursor-pointer" : ""}`}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className={`h-3.5 w-3.5 ${accent}`} />}
        <p className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground">{label}</p>
      </div>
      <p className={`text-xl font-bold mt-1 ${accent}`} style={{ fontFamily: "Outfit" }}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground font-mono">{sub}</p>}
      {onClick && <p className="text-[10px] text-muted-foreground mt-0.5">klik untuk lihat daftar</p>}
    </Tag>
  );
}

function BillListDialog({ list, onClose }) {
  const navigate = useNavigate();
  const rows = list?.rows || [];
  return (
    <Dialog open={!!list} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dash-bill-list-dialog">
        <DialogHeader>
          <DialogTitle>{list?.title}</DialogTitle>
          <DialogDescription>{rows.length} tagihan · Total <span className="font-mono font-semibold" data-testid="dash-bill-list-total">{formatRupiah(rows.reduce((t, b) => t + (b.amount || 0), 0))}</span></DialogDescription>
        </DialogHeader>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6" data-testid="dash-bill-list-empty">Tidak ada data.</p> : (
          <div className="space-y-2">
            {rows.map((b) => (
              <button key={b.id} type="button" onClick={() => navigate("/vendor-bills")} data-testid={`dash-bill-list-row-${b.id}`}
                className="w-full text-left flex items-start justify-between gap-3 px-3 py-2 rounded-lg border hover:bg-secondary/60 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{b.vendor_name}</p>
                  <p className="text-xs text-muted-foreground">{[b.company_name, PAYMENT_TYPE_LABEL[b.payment_type], (b.notas || []).map((n) => n.nomor_nota).filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</p>
                  <p className="text-xs text-muted-foreground">Jatuh tempo {formatDate(b.due_date)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold font-mono">{formatRupiah(b.amount)}</p>
                  <p className={`text-[11px] font-medium ${b.days_to_due < 0 ? "text-rose-600" : "text-amber-600"}`}>
                    {b.days_to_due < 0 ? `Terlambat ${Math.abs(b.days_to_due)} hari` : b.days_to_due === 0 ? "Hari ini" : `${b.days_to_due} hari lagi`}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
